import { useCallback, useState } from 'react'
import { createContainer } from 'unstated-next'

import useNotify from '../../../../hooks/use-notify'
import useSwr from '../../../../hooks/use-swr'
import useToggle from '../../../../hooks/use-toggle'
import PaymentApis from '../../../../modules/payment/api/payment'
import AuthStore from '../../../../modules/user/stores/auth-store'
import PricingApis from '../../apis'
import SubscriptionsParser, {
  ActiveSubscriptionParser,
  pricingReviewsParser,
  SubscriptionInvitationsParser,
} from '../utils/parser'

const PricingStore = createContainer(() => {
  const notify = useNotify()
  const [bill, setBill] = useState(null)
  const [showMonthly, toggleMonthly] = useToggle(false)

  const { loggedIn } = AuthStore.useContainer()

  const { data, error } = useSwr({
    parser: SubscriptionsParser,
    url: 'subscription/models',
  })

  const { data: existing, error: existingError } = useSwr({
    condition: loggedIn,
    parser: ActiveSubscriptionParser,
    url: `subscriptions`,
  })

  const { data: invites, revalidate } = useSwr({
    condition: loggedIn,
    parser: SubscriptionInvitationsParser,
    url: `invitations`,
  })

  const reviews = useSwr({
    parser: pricingReviewsParser,
    url: `review/v3/WHITELISTED/limit/15`,
  })

  const purchaseSubscription = useCallback(async (id) => {
    const { success, result } = await PricingApis.purchaseSubscription(id)

    if (success) {
      setBill(result)
    }

    return success
  }, [])

  const sendInvite = useCallback(
    async (email) => {
      const { success } = await PricingApis.sendSubscriptionInvite(email)

      if (success) {
        await revalidate()
        notify('success', `Successfully sent the invite offer to ${email}`)
      }

      return success
    },
    [notify, revalidate],
  )

  const deleteInvite = useCallback(
    async (id) => {
      const { success } = await PricingApis.deleteSubscriptionInvite(id)

      if (success) {
        await revalidate()
        notify('success', 'Successfully deleted the invite.')
      }

      return success
    },
    [notify, revalidate],
  )

  const addDiscountCode = useCallback(
    async (code) => {
      if (!code) {
        return false
      }

      const {
        success,
        result: updatedBill,
      } = await PaymentApis.redeemDiscountCoupon(bill?.id, code.toUpperCase())

      if (success) {
        setBill(updatedBill)
      }

      return success
    },
    [bill?.id],
  )

  return {
    addDiscountCode,
    bill,
    data,
    deleteInvite,
    existing,
    invites,
    loadFailed:
      (Boolean(error) && !data) || (Boolean(existingError) && !existing),
    loading: (!data && !error) || (!existing && !existingError && loggedIn),
    purchaseSubscription,
    reviews,
    sendInvite,
    showMonthly,
    toggleMonthly,
  }
})

export default PricingStore
